import React, { useEffect, useRef, useState } from 'react'
import { MeetingDetailsScreen } from '../MeetingDetailsScreen'
import { createMeeting, validateMeeting, getToken } from '../../api'
import ConfirmBox from '../ConfirmBox'
import { Constants, useMediaDevice } from '@videosdk.live/react-sdk'
import useMediaStream from '../../hooks/useMediaStream'
import useIsMobile from '../../hooks/useIsMobile'
import WebcamOffIcon from '../../icons/WebcamOffIcon'
import WebcamOnIcon from '../../icons/Bottombar/WebcamOnIcon'
import MicOffIcon from '../../icons/MicOffIcon'
import MicOnIcon from '../../icons/Bottombar/MicOnIcon'
import MicPermissionDenied from '../../icons/MicPermissionDenied'
import CameraPermissionDenied from '../../icons/CameraPermissionDenied'
import DropDown from '../DropDown'
import DropDownCam from '../DropDownCam'
import DropDownSpeaker from '../DropDownSpeaker'
import NetworkStats from '../NetworkStats'
import { useMeetingAppContext } from '../../MeetingAppContextDef'
import { toast } from 'react-toastify'
import { useUser } from '@clerk/clerk-react'
import { useRouter } from '@/hooks/useRouter'
import { NavBar } from '../nav-bar'

type IdLabel = { id?: string; label?: string }

type ButtonWithTooltipProps = {
  onClick: () => void
  onState: boolean
  OnIcon: React.ComponentType<{ fillcolor?: string }>
  OffIcon: React.ComponentType<{ fillcolor?: string }>
}

interface JoiningScreenProps {
  participantName: string
  setParticipantName: React.Dispatch<React.SetStateAction<string>>
  setMeetingId: (id: string) => void
  setToken: (token: string) => void
  onClickStartMeeting: () => void
  micOn: boolean
  webcamOn: boolean
  setWebcamOn: (v: boolean) => void
  setMicOn: (v: boolean) => void
  customAudioStream: MediaStream | null
  setCustomAudioStream: (s: MediaStream | null) => void
  setCustomVideoStream: (s: MediaStream | null) => void
  customVideoStream: MediaStream | null
  startMeeting?: () => void
  setIsMeetingLeft?: (v: boolean) => void
}

type PermissionMap = Map<string, boolean>

export function JoiningScreen({
  participantName,
  setParticipantName,
  setMeetingId,
  setToken,
  onClickStartMeeting,
  micOn,
  webcamOn,
  setWebcamOn,
  setMicOn,
  customAudioStream,
  setCustomAudioStream,
  setCustomVideoStream,
  customVideoStream,
}: JoiningScreenProps) {
  const {
    selectedWebcam,
    selectedMic,
    setSelectedMic,
    setSelectedWebcam,
    setSelectedSpeaker,
    isCameraPermissionAllowed,
    isMicrophonePermissionAllowed,
    setIsCameraPermissionAllowed,
    setIsMicrophonePermissionAllowed,
  }: {
    selectedWebcam: IdLabel
    selectedMic: IdLabel
    setSelectedMic: (val: IdLabel) => void
    setSelectedWebcam: (val: IdLabel) => void
    setSelectedSpeaker: (val: IdLabel) => void
    isCameraPermissionAllowed: boolean
    isMicrophonePermissionAllowed: boolean
    setIsCameraPermissionAllowed: (val: boolean) => void
    setIsMicrophonePermissionAllowed: (val: boolean) => void
  } = useMeetingAppContext()

  const [{ webcams, mics, speakers }, setDevices] = useState<{
    webcams: MediaDeviceInfo[]
    mics: MediaDeviceInfo[]
    speakers: MediaDeviceInfo[]
  }>({
    webcams: [],
    mics: [],
    speakers: [],
  })

  const {
    getVideoTrack,
    getAudioTrack,
  }: {
    getVideoTrack: (opts: { webcamId?: string }) => Promise<MediaStream>
    getAudioTrack: (opts: { micId?: string }) => Promise<MediaStream>
  } = useMediaStream()

  const { checkPermissions, getCameras, getMicrophones, requestPermission, getPlaybackDevices } =
    useMediaDevice({ onDeviceChanged })

  const [audioTrack, setAudioTrack] = useState<MediaStreamTrack | null>(null)
  const [videoTrack, setVideoTrack] = useState<MediaStreamTrack | null>(null)
  const [dlgMuted, setDlgMuted] = useState(false)
  const [dlgDevices, setDlgDevices] = useState(false)
  const [didDeviceChange, setDidDeviceChange] = useState(false)

  const videoPlayerRef = useRef<HTMLVideoElement | null>(null)
  const videoTrackRef = useRef<MediaStreamTrack | null>(null)
  const audioTrackRef = useRef<MediaStreamTrack | null>(null)
  const audioAnalyserIntervalRef = useRef<number | undefined>(undefined)
  const permissonAvaialble = useRef<{
    isCameraPermissionAllowed: boolean
    isMicrophonePermissionAllowed: boolean
  } | null>(null)
  const webcamRef = useRef<boolean>(false)
  const micRef = useRef<boolean>(false)
  const isMobile = useIsMobile()
  const { isLoaded, user } = useUser()
  const { query, push }: { query: Record<string, any>; push: (path: string) => void } = useRouter()
  const camStartReqRef = useRef(0)
  const micStartReqRef = useRef(0)

  useEffect(() => {
    if (isLoaded && !user && query.id) {
      localStorage.setItem('tempHallId', String(query.id))
      push(`/sign-in`)
    }
  }, [user, query.id, isLoaded, push])

  useEffect(() => {
    webcamRef.current = webcamOn
  }, [webcamOn])

  useEffect(() => {
    micRef.current = micOn
  }, [micOn])

  useEffect(() => {
    permissonAvaialble.current = {
      isCameraPermissionAllowed,
      isMicrophonePermissionAllowed,
    }
  }, [isCameraPermissionAllowed, isMicrophonePermissionAllowed])

  useEffect(() => {
    if (micOn) {
      audioTrackRef.current = audioTrack
      startMuteListener()
    }
  }, [micOn, audioTrack])

  useEffect(() => {
    const player = videoPlayerRef.current
    if (!player) return

    const isPlaying =
      player.currentTime > 0 &&
      !player.paused &&
      !player.ended &&
      player.readyState > (player as any).HAVE_CURRENT_DATA

    if (webcamOn && videoTrack) {
      const ms = new MediaStream([videoTrack])
      player.srcObject = ms
      if (player.pause && !isPlaying) {
        player.play().catch(() => {})
      }
    } else {
      player.srcObject = null
    }
  }, [webcamOn, videoTrack])

  useEffect(() => {
    getCameraDevices()
  }, [isCameraPermissionAllowed])

  useEffect(() => {
    getAudioDevices()
  }, [isMicrophonePermissionAllowed])

  useEffect(() => {
    checkMediaPermission()
  }, []) // intentional

  useEffect(() => {
    videoTrackRef.current = videoTrack || null
  }, [videoTrack])

  useEffect(() => {
    if (webcamOn && !videoTrack && selectedWebcam?.id) {
      getDefaultMediaTracks({ mic: false, webcam: true })
    }
  }, [selectedWebcam?.id, webcamOn])

  useEffect(() => {
    if (micOn && !audioTrack && selectedMic?.id) {
      getDefaultMediaTracks({ mic: true, webcam: false })
    }
  }, [selectedMic?.id, micOn])

  const _toggleWebcam = () => {
    if (webcamOn) {
      try {
        videoTrackRef.current?.stop()
      } catch {}
      try {
        const so = videoPlayerRef.current?.srcObject as MediaStream | null
        so?.getTracks().forEach((t) => t.stop())
      } catch {}
      try {
        customVideoStream?.getTracks().forEach((t) => t.stop())
      } catch {}
      if (videoPlayerRef.current) videoPlayerRef.current.srcObject = null
      setVideoTrack(null)
      setCustomVideoStream(null)
      setWebcamOn(false)
    } else {
      getDefaultMediaTracks({ mic: false, webcam: true })
      setWebcamOn(true)
    }
  }

  const _toggleMic = () => {
    const track = audioTrackRef.current
    if (micOn) {
      if (track) {
        track.stop()
        setAudioTrack(null)
        setCustomAudioStream(null)
        setMicOn(false)
      }
    } else {
      getDefaultMediaTracks({ mic: true, webcam: false })
      setMicOn(true)
    }
  }

  const changeWebcam = async (deviceId: string) => {
    if (webcamOn) {
      videoTrackRef.current?.stop()
      const stream = await getVideoTrack({ webcamId: deviceId })
      setCustomVideoStream(stream)
      const track = stream?.getVideoTracks()[0] ?? null
      setVideoTrack(track)
    }
    localStorage.setItem('lastCamId', deviceId)
  }

  const changeMic = async (deviceId: string) => {
    if (micOn) {
      audioTrackRef.current?.stop()
      const stream = await getAudioTrack({ micId: deviceId })
      setCustomAudioStream(stream)
      const track = stream?.getAudioTracks()[0] ?? null
      if (audioAnalyserIntervalRef.current !== undefined) {
        clearInterval(audioAnalyserIntervalRef.current)
      }
      setAudioTrack(track)
    }
    localStorage.setItem('lastMicId', deviceId)
  }

  const getDefaultMediaTracks = async ({ mic, webcam }: { mic: boolean; webcam: boolean }) => {
    if (mic) {
      const req = ++micStartReqRef.current
      try {
        const fallbackMicId =
          selectedMic?.id ?? localStorage.getItem('lastMicId') ?? mics[0]?.deviceId
        const stream = await getAudioTrack(fallbackMicId ? { micId: fallbackMicId } : {})
        if (micStartReqRef.current !== req) {
          stream?.getAudioTracks().forEach((t) => t.stop())
          return
        }
        setCustomAudioStream(stream)
        const track = stream?.getAudioTracks()?.[0]
        if (track) {
          setAudioTrack(track)
          setMicOn(true)
        }
      } catch (e) {
      }
    }

    if (webcam) {
      const req = ++camStartReqRef.current
      try {
        const fallbackCamId =
          selectedWebcam?.id ?? localStorage.getItem('lastCamId') ?? webcams[0]?.deviceId
        const stream = await getVideoTrack(fallbackCamId ? { webcamId: fallbackCamId } : {})
        if (camStartReqRef.current !== req) {
          stream?.getVideoTracks().forEach((t) => t.stop())
          return
        }
        const newTrack = stream?.getVideoTracks()?.[0]
        if (newTrack) {
          setCustomVideoStream(stream)
          setVideoTrack((prev) => {
            if (prev && prev !== newTrack) {
              try {
                prev.stop()
              } catch {}
            }
            return newTrack
          })
          setWebcamOn(true)
        }
      } catch (e) {
      }
    }
  }

  async function startMuteListener() {
    const currentAudioTrack = audioTrackRef.current
    if (currentAudioTrack) {
      if ((currentAudioTrack as any).muted) {
        setDlgMuted(true)
      }
      currentAudioTrack.addEventListener('mute', () => setDlgMuted(true))
    }
  }

  const isFirefox =
    typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('firefox') > -1

  async function requestAudioVideoPermission(mediaType?: any) {
    try {
      const permission = await requestPermission(mediaType)

      if (isFirefox) {
        const isVideoAllowed = permission.get('video')
        setIsCameraPermissionAllowed(!!isVideoAllowed)
        if (isVideoAllowed) {
          setWebcamOn(true)
          await getDefaultMediaTracks({ mic: false, webcam: true })
        }

        const isAudioAllowed = permission.get('audio')
        setIsMicrophonePermissionAllowed(!!isAudioAllowed)
        if (isAudioAllowed) {
          setMicOn(true)
          await getDefaultMediaTracks({ mic: true, webcam: false })
        }
      }

      if (mediaType === Constants.permission.AUDIO) {
        const isAudioAllowed = permission.get(Constants.permission.AUDIO)
        setIsMicrophonePermissionAllowed(!!isAudioAllowed)
        if (isAudioAllowed) {
          setMicOn(true)
          await getDefaultMediaTracks({ mic: true, webcam: false })
        }
      }

      if (mediaType === Constants.permission.VIDEO) {
        const isVideoAllowed = permission.get(Constants.permission.VIDEO)
        setIsCameraPermissionAllowed(!!isVideoAllowed)
        if (isVideoAllowed) {
          setWebcamOn(true)
          await getDefaultMediaTracks({ mic: false, webcam: true })
        }
      }
    } catch (ex) {
      // no-op
    }
  }

  function onDeviceChanged() {
    setDidDeviceChange(true)
    getCameraDevices()
    getAudioDevices()
    getDefaultMediaTracks({ mic: micRef.current, webcam: webcamRef.current })
  }

  const checkMediaPermission = async () => {
    try {
      const perms = await checkPermissions()
      const camOK = !!perms.get(Constants.permission.VIDEO)
      const micOK = !!perms.get(Constants.permission.AUDIO)

      setIsCameraPermissionAllowed(camOK)
      setIsMicrophonePermissionAllowed(micOK)

      if (micOK) {
        const micDevices = await getMicrophones()
        const lastMicId = localStorage.getItem('lastMicId')
        const chosenMic = micDevices.find((m) => m.deviceId === lastMicId) ?? micDevices[0]
        if (chosenMic) setSelectedMic({ id: chosenMic.deviceId, label: chosenMic.label })
        await getDefaultMediaTracks({ mic: true, webcam: false })
      } else {
        await requestAudioVideoPermission(Constants.permission.AUDIO)
      }

      if (camOK) {
        const camDevices = await getCameras()
        const lastCamId = localStorage.getItem('lastCamId')
        const chosenCam = camDevices.find((c) => c.deviceId === lastCamId) ?? camDevices[0]
        if (chosenCam) setSelectedWebcam({ id: chosenCam.deviceId, label: chosenCam.label })
        await getDefaultMediaTracks({ mic: false, webcam: true })
      } else {
        await requestAudioVideoPermission(Constants.permission.VIDEO)
      }
    } catch (error) {
      await requestAudioVideoPermission()
    }
  }

  const getCameraDevices = async () => {
    try {
      if (permissonAvaialble.current?.isCameraPermissionAllowed) {
        const cams = await getCameras()
        setSelectedWebcam({
          id: cams[0]?.deviceId,
          label: cams[0]?.label,
        })
        setDevices((d) => ({ ...d, webcams: cams as MediaDeviceInfo[] }))
      }
    } catch (err) {
      // no-op
    }
  }

  const getAudioDevices = async () => {
    try {
      if (permissonAvaialble.current?.isMicrophonePermissionAllowed) {
        const micDevices = await getMicrophones()
        const spks = await getPlaybackDevices()
        const hasMic = micDevices.length > 0
        if (hasMic) {
          startMuteListener()
        }
        setSelectedSpeaker({
          id: spks[0]?.deviceId,
          label: spks[0]?.label,
        })
        setSelectedMic({
          id: micDevices[0]?.deviceId,
          label: micDevices[0]?.label,
        })
        setDevices((d) => ({
          ...d,
          mics: micDevices as MediaDeviceInfo[],
          speakers: spks as MediaDeviceInfo[],
        }))
      }
    } catch (err) {
      // no-op
    }
  }

  useEffect(() => {
    getAudioDevices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ButtonWithTooltip: React.FC<ButtonWithTooltipProps> = ({
    onClick,
    onState,
    OnIcon,
    OffIcon,
  }) => {
    const btnRef = useRef<HTMLButtonElement | null>(null)
    return (
      <div>
        <button
          ref={btnRef}
          onClick={onClick}
          className={`rounded-full min-w-auto w-12 h-12 flex items-center justify-center 
            ${onState ? 'bg-white' : 'bg-red-650 text-white'}`}
        >
          {onState ? <OnIcon fillcolor="#050A0E" /> : <OffIcon fillcolor="#fff" />}
        </button>
      </div>
    )
  }

  return (
    <>
      <NavBar />
      {/* Full-height wrapper that honors --vh, with a SINGLE scroll layer */}
      <div
        className="flex flex-col min-h-[calc(var(--vh)*100)] bg-background"
        style={{ WebkitOverflowScrolling: 'touch' as any }}
      >
        <div
          className="flex-1 overflow-y-auto noScrollbar"
          style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' as any }}
        >
          <div className="flex flex-col md:flex-row items-center justify-center md:m-[72px] m-6">
            <div className="container grid md:grid-flow-col grid-flow-row">
              <div className="grid grid-cols-12">
                {/* LEFT: self preview */}
                <div className="md:col-span-7 2xl:col-span-7 col-span-12">
                  <div className="flex items-center justify-center p-1.5 sm:p-4 lg:p-6">
                    <div className="relative w-full md:pl-4 sm:pl-10 pl-5 md:pr-4 sm:pr-10 pr-5">
                      <div className="w-full relative" style={{ height: '55vh', maxHeight: 520 }}>
                        <video
                          autoPlay
                          playsInline
                          muted
                          ref={videoPlayerRef}
                          controls={false}
                          style={{ backgroundColor: '#1c1c1c' }}
                          className="rounded-[10px] h-full w-full object-cover flex items-center justify-center flip"
                        />
                        {!isMobile ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            {!webcamOn ? (
                              <p className="text-xl xl:text-lg 2xl:text-xl text-white">
                                The camera is off
                              </p>
                            ) : null}
                          </div>
                        ) : null}

                        <div className="absolute xl:bottom-6 bottom-4 left-0 right-0">
                          <div className="container grid grid-flow-col gap-4 items-center justify-center md:-m-2">
                            {isMicrophonePermissionAllowed ? (
                              <ButtonWithTooltip
                                onClick={_toggleMic}
                                onState={micOn}
                                OnIcon={MicOnIcon}
                                OffIcon={MicOffIcon}
                              />
                            ) : (
                              <MicPermissionDenied />
                            )}

                            {isCameraPermissionAllowed ? (
                              <ButtonWithTooltip
                                onClick={_toggleWebcam}
                                onState={webcamOn}
                                OnIcon={WebcamOnIcon}
                                OffIcon={WebcamOffIcon}
                              />
                            ) : (
                              <CameraPermissionDenied />
                            )}
                          </div>
                        </div>
                      </div>

                      {!isMobile && (
                        <>
                          <div className="absolute top-2 right-10">
                            <NetworkStats />
                          </div>

                          <div className="flex mt-3">
                            {/* Firefox hides device pickers */}
                            {!isFirefox && (
                              <>
                                <DropDown
                                  mics={mics}
                                  changeMic={changeMic}
                                  customAudioStream={customAudioStream}
                                  audioTrack={audioTrack}
                                  micOn={micOn}
                                  didDeviceChange={didDeviceChange}
                                  setDidDeviceChange={setDidDeviceChange}
                                />
                                <DropDownSpeaker speakers={speakers} />
                                <DropDownCam changeWebcam={changeWebcam} webcams={webcams} />
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* RIGHT: meeting details */}
                <div className="md:col-span-5 2xl:col-span-5 col-span-12 md:relative">
                  <div className="flex flex-1 flex-col items-center justify-center xl:m-16 lg:m-6 md:mt-9 lg:mt-14 xl:mt-20 mt-3 md:absolute md:inset-0">
                    <MeetingDetailsScreen
                      participantName={participantName}
                      setParticipantName={setParticipantName}
                      onClickStartMeeting={onClickStartMeeting}
                      onClickJoin={async (id: string) => {
                        const token = await getToken(id)
                        const { meetingId, err } = await validateMeeting({ roomId: id, token })
                        if (meetingId === id) {
                          setToken(token)
                          setMeetingId(id)
                          onClickStartMeeting()
                        } else {
                          toast(`${err}`, {
                            position: 'bottom-left',
                            autoClose: 4000,
                            hideProgressBar: true,
                            closeButton: false,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: 'light',
                          })
                        }
                      }}
                      _handleOnCreateMeeting={async () => {
                        const token = await getToken()
                        const { meetingId, err } = await createMeeting({ token })
                        if (meetingId) {
                          setToken(token)
                          setMeetingId(meetingId)
                        }
                        return { meetingId, err }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* dialogs */}
        <ConfirmBox
          open={dlgMuted}
          successText="OKAY"
          rejectText=""
          onSuccess={() => setDlgMuted(false)}
          onReject={() => setDlgMuted(false)}
          title="System mic is muted"
          subTitle="You're default microphone is muted, please unmute it or increase audio
            input volume from system settings."
          subTitleColor=""
        />
        <ConfirmBox
          open={dlgDevices}
          successText="DISMISS"
          rejectText=""
          onSuccess={() => setDlgDevices(false)}
          onReject={() => setDlgDevices(false)}
          title="Mic or webcam not available"
          subTitle="Please connect a mic and webcam to speak and share your video in the meeting. You can also join without them."
          subTitleColor=""
        />
      </div>
    </>
  )
}
